/**
 * Character-scanning KML parser for range-contour import (no regex).
 * Extracts each Placemark's name and polygon coordinate rings, classifying the
 * boundary as air- or sea-space from keywords in the name. Resilient to
 * malformed placemarks — bad entries are skipped, never thrown.
 */

import type { GeoPoint } from '../types/drawing.js';
import type { BoundaryContour, BoundaryCategory } from '../types/boundary.js';

/** Minimum vertices for a usable polygon ring */
const MIN_RING_POINTS = 3;

/** Characters that legally terminate an opening tag name */
const TAG_DELIMITERS = new Set(['>', ' ', '\t', '\n', '\r', '/']);

/** Whitespace characters separating coordinate tuples */
const WHITESPACE = new Set([' ', '\t', '\n', '\r']);

/** Name keywords that classify a boundary as sea-space */
const SEA_KEYWORDS = ['sea', 'msa', 'surveillance', 'maritime', 'water', 'ocean', 'marine'];

/** Standard XML entities to decode in element text */
const XML_ENTITIES: ReadonlyArray<[string, string]> = [
  ['&amp;', '&'],
  ['&lt;', '<'],
  ['&gt;', '>'],
  ['&quot;', '"'],
  ['&apos;', "'"],
];

/** Parse KML text into boundary contours (one per polygon ring). */
export function parseKmlBoundaries(kml: string): BoundaryContour[] {
  const contours: BoundaryContour[] = [];

  for (const placemark of innerBlocks(kml, 'Placemark')) {
    const name = decodeEntities((firstBlock(placemark, 'name') ?? 'Boundary').trim());
    const category = classify(name);
    for (const coordsText of coordinateBlocks(placemark)) {
      const points = parseCoordinates(coordsText);
      if (points.length >= MIN_RING_POINTS) contours.push({ name, category, points });
    }
  }
  return contours;
}

/** Coordinate blocks of a placemark: one per Polygon, falling back to any. */
function coordinateBlocks(placemark: string): string[] {
  const polygons = innerBlocks(placemark, 'Polygon');
  if (polygons.length === 0) return innerBlocks(placemark, 'coordinates');

  const blocks: string[] = [];
  for (const polygon of polygons) {
    const coords = firstBlock(polygon, 'coordinates');
    if (coords !== null) blocks.push(coords);
  }
  return blocks;
}

/** Inner text of every `<tag>…</tag>` in source. */
function innerBlocks(source: string, tag: string): string[] {
  const results: string[] = [];
  const close = `</${tag}>`;
  let cursor = 0;

  while (cursor < source.length) {
    const contentStart = openTagEnd(source, tag, cursor);
    if (contentStart === -1) break;
    const closeStart = source.indexOf(close, contentStart);
    if (closeStart === -1) break;
    results.push(source.slice(contentStart, closeStart));
    cursor = closeStart + close.length;
  }
  return results;
}

/** Inner text of the first `<tag>…</tag>`, or null. */
function firstBlock(source: string, tag: string): string | null {
  return innerBlocks(source, tag)[0] ?? null;
}

/**
 * Index just past the `>` of the next opening `<tag …>`, or -1.
 * Verifies the character after the tag name is a real delimiter so `<name>`
 * never matches `<namespace>`.
 */
function openTagEnd(source: string, tag: string, from: number): number {
  const needle = `<${tag}`;
  let index = source.indexOf(needle, from);

  while (index !== -1) {
    const after = source[index + needle.length];
    if (after !== undefined && TAG_DELIMITERS.has(after)) {
      const gt = source.indexOf('>', index);
      return gt === -1 ? -1 : gt + 1;
    }
    index = source.indexOf(needle, index + needle.length);
  }
  return -1;
}

/** Parse a KML coordinate string (`lon,lat[,alt]` tuples) into points. */
function parseCoordinates(text: string): GeoPoint[] {
  const points: GeoPoint[] = [];

  for (const tuple of splitWhitespace(text)) {
    const parts = tuple.split(',');
    if (parts.length < 2) continue;
    const lon = Number(parts[0]);
    const lat = Number(parts[1]);
    if (Number.isFinite(lat) && Number.isFinite(lon)) points.push({ lat, lon });
  }
  return points;
}

/** Split text on runs of whitespace (no regex). */
function splitWhitespace(text: string): string[] {
  const tokens: string[] = [];
  let current = '';

  for (const char of text) {
    if (WHITESPACE.has(char)) {
      if (current) tokens.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  if (current) tokens.push(current);
  return tokens;
}

/** Classify a boundary as sea-space when its name matches a sea keyword. */
function classify(name: string): BoundaryCategory {
  const lower = name.toLowerCase();
  for (const keyword of SEA_KEYWORDS) {
    if (lower.includes(keyword)) return 'sea';
  }
  return 'air';
}

/** Decode the five standard XML entities in element text (no regex). */
function decodeEntities(text: string): string {
  let result = text;
  for (const [entity, char] of XML_ENTITIES) {
    result = result.split(entity).join(char);
  }
  return result;
}
